import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function EvidenceRegisterModal({ serverId, serviceUserId, onClose }) {
    const [evidenceItems, setEvidenceItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchEvidence = async () => {
            setLoading(true);
            try {
                // Fetch the service user doc for dnacpr flags
                const userRef = doc(db, 'servers', serverId, 'serviceUsers', serviceUserId);
                const userSnap = await getDoc(userRef);
                const userData = userSnap.exists() ? userSnap.data() : {};
                
                // Fetch complianceEvidence overrides
                const evRef = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'complianceEvidence');
                const evSnap = await getDocs(query(evRef));
                const evidenceMap = {};
                evSnap.docs.forEach(d => {
                    evidenceMap[d.id] = d.data();
                });

                // Fetch documents for checking file presence
                const docsRef = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'documents');
                const docsSnap = await getDocs(query(docsRef));
                const docsMap = docsSnap.docs.map(d => (d.data().name || '').toLowerCase());
                
                const hasDoc = (needle) => docsMap.some(d => d.includes(needle.toLowerCase()));
                const isOverdue = (dateVal) => {
                    if (!dateVal) return false;
                    const ms = dateVal.seconds ? dateVal.seconds * 1000 : new Date(dateVal).getTime();
                    return ms < Date.now();
                };

                const buildItem = (id, title, liveFound, source) => {
                    const docData = evidenceMap[id] || {};
                    const reviewDate = docData.reviewDate;
                    const expiryDate = docData.expiryDate;
                    const na = docData.na === true || docData.isNotApplicable === true;
                    const note = docData.notes || docData.process || '';
                    
                    const hasUpload = liveFound || (docData.files && docData.files.length > 0) || hasDoc(title);
                    
                    let status = 'COMPLETE';
                    let statusColor = '#166534'; // green
                    let statusBg = '#dcfce7';

                    if (na) {
                        status = 'N/A';
                        statusColor = '#4b5563'; // gray
                        statusBg = '#f3f4f6';
                    } else if (!hasUpload) {
                        status = 'MISSING';
                        statusColor = '#991b1b'; // red
                        statusBg = '#fee2e2';
                    } else if (isOverdue(reviewDate) || isOverdue(expiryDate)) {
                        status = 'REVIEW DUE';
                        statusColor = '#9a3412'; // orange
                        statusBg = '#ffedd5';
                    }

                    return {
                        id,
                        title,
                        status,
                        statusColor,
                        statusBg,
                        hasFile: hasUpload,
                        source,
                        notes: !note.trim() ? 'No process notes recorded' : note,
                        reviewDate: reviewDate ? new Date(reviewDate.seconds ? reviewDate.seconds * 1000 : reviewDate).toLocaleDateString() : null,
                        expiryDate: expiryDate ? new Date(expiryDate.seconds ? expiryDate.seconds * 1000 : expiryDate).toLocaleDateString() : null,
                    };
                };

                const items = [
                    buildItem('dnacpr', 'DNACPR form', !!userData.dnacpr || !!userData.dnacprFile || hasDoc('dnacpr'), 'Edit profile / documents'),
                    buildItem('capacity', 'Mental capacity assessment', false, 'Mental Capacity Module'), // Simplified, assumes false liveFound without querying mca collection
                    buildItem('respect', 'ReSPECT form', hasDoc('respect'), 'Document uploads'),
                    buildItem('chc', 'Continuing Healthcare (CHC) checklist', hasDoc('chc') || hasDoc('healthcare'), 'Document uploads'),
                    buildItem('deprivation', 'Deprivation of Liberty Safeguards (DoLS)', hasDoc('dols') || hasDoc('deprivation'), 'Document uploads'),
                    buildItem('behavior', 'Behavior support plan', false, 'Care plans'),
                    buildItem('risk', 'Risk assessment', false, 'Risk assessments module'),
                    buildItem('medication', 'Medication administration record (MAR)', false, 'Medication module'),
                    buildItem('wishes', 'Wishes and preferences', false, 'Wishes and preferences module')
                ];

                setEvidenceItems(items);
            } catch (err) {
                console.error("Error fetching evidence register:", err);
                setError("Failed to load evidence register.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId) {
            fetchEvidence();
        }
    }, [serverId, serviceUserId]);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Compliance Evidence Register</h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>Overview of critical documents and assessments</p>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Building evidence register...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {evidenceItems.map((item) => (
                                <div key={item.id} style={styles.recordCard}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>{item.title}</h3>
                                            <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                                Source: {item.source}
                                            </div>
                                        </div>
                                        <span style={{ 
                                            display: 'inline-block', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 'bold',
                                            backgroundColor: item.statusBg, color: item.statusColor, border: `1px solid ${item.statusColor}40`
                                        }}>
                                            {item.status}
                                        </span>
                                    </div>
                                    
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', borderTop: '1px solid #f3f4f6', paddingTop: '12px', marginTop: '12px' }}>
                                        <div style={{ flex: '1', minWidth: '150px' }}>
                                            <label style={styles.fieldLabel}>Review Date</label>
                                            <div style={styles.fieldValue}>{item.reviewDate || 'Not set'}</div>
                                        </div>
                                        <div style={{ flex: '1', minWidth: '150px' }}>
                                            <label style={styles.fieldLabel}>Expiry Date</label>
                                            <div style={styles.fieldValue}>{item.expiryDate || 'Not set'}</div>
                                        </div>
                                    </div>
                                    <div style={{ marginTop: '12px' }}>
                                        <label style={styles.fieldLabel}>Process Notes</label>
                                        <div style={{ fontSize: '13px', color: '#4b5563' }}>{item.notes}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    fieldLabel: { display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', marginBottom: '4px', fontWeight: 'bold' },
    fieldValue: { fontSize: '14px', color: '#111827' }
};
